import { useTheme } from "@/contexts/ThemeProvider";
import { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
} from "@/components/ui/popover";
import {
  WORKSPACE_COLORS,
  WORKSPACE_ICONS,
} from "@/lib/constants/workspace-customization";

/**
 * ColorIconChooser - A popover component for selecting workspace colors and icons
 *
 * Uses PopoverAnchor (not PopoverTrigger) to avoid click handler conflicts when
 * the children element is a CollapsibleTrigger or other interactive element.
 * The popover is controlled via isOpen prop, not via click on children.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls popover visibility
 * @param {function} props.onOpenChange - Callback when popover open state changes
 * @param {function} props.onColorSelect - Callback when a color is selected, receives color object
 * @param {function} props.onIconSelect - Callback when an icon is selected, receives icon object
 * @param {string} props.currentColor - Currently selected color hex value
 * @param {string} props.currentIcon - Currently selected icon value
 * @param {React.ReactNode} props.children - Anchor element for positioning (not a click trigger)
 */
const ColorIconChooser = ({
  isOpen = false,
  onOpenChange,
  onColorSelect,
  onIconSelect,
  currentColor = "#f8f9fa",
  currentIcon = "folder",
  children,
}) => {
  const { resolvedTheme } = useTheme();
  const openTimeRef = useRef(null);

  // Track when the popover opens to prevent immediate close events
  useEffect(() => {
    if (isOpen) {
      openTimeRef.current = Date.now();
    }
  }, [isOpen]);

  // Prevent close events that happen within 200ms of opening
  // This handles race conditions from dropdown menu closing
  const handleInteractOutside = (e) => {
    if (openTimeRef.current && Date.now() - openTimeRef.current < 200) {
      e.preventDefault();
    }
  };

  // In light mode, convert white to black for visibility
  const getDisplayColor = (colorValue) => {
    if (colorValue === "#f8f9fa" && resolvedTheme === "light") {
      return "#000000";
    }
    return colorValue;
  };

  const handleColorClick = (color) => {
    onColorSelect?.(color);
  };

  const handleIconClick = (icon) => {
    onIconSelect?.(icon);
  };

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange} modal={false}>
      <PopoverAnchor asChild>{children}</PopoverAnchor>
      <PopoverContent
        className={cn(
          "z-[10001] max-w-xs rounded-2xl",
          "bg-white dark:bg-zinc-800",
          "shadow-lg",
          "py-1.5 px-3",
          "min-w-[min(280px,95vw)]",
          "max-h-[var(--radix-popover-content-available-height)]",
          "overflow-y-auto select-none",
          "border border-gray-200 dark:border-zinc-700"
        )}
        style={{ pointerEvents: "auto" }}
        align="start"
        side="bottom"
        sideOffset={5}
        onInteractOutside={handleInteractOutside}
        onPointerDownOutside={handleInteractOutside}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* Color Selection Grid */}
        <div
          role="group"
          aria-label="Color selection"
          className="grid grid-cols-[repeat(6,36px)] gap-1 py-3"
        >
          {WORKSPACE_COLORS.map((color) => {
            const isSelected = currentColor === color.value;
            return (
              <button
                key={color.name}
                role="menuitemradio"
                aria-checked={isSelected}
                tabIndex={0}
                className={cn(
                  "group m-0 flex h-9 w-9 items-center justify-center rounded-full p-0",
                  "origin-center active:scale-95 transition-transform",
                  "hover:bg-gray-100 dark:hover:bg-zinc-700",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400"
                )}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  handleColorClick(color);
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <span
                  className="grid h-6 w-6 place-items-center rounded-full"
                  style={{ backgroundColor: getDisplayColor(color.value) }}
                >
                  {isSelected && (
                    <span className="inline-block aspect-square h-4 w-auto origin-center rounded-full outline outline-2 outline-white dark:outline-black" />
                  )}
                </span>
                <span className="sr-only">{color.name}</span>
              </button>
            );
          })}
        </div>

        {/* Separator */}
        <div
          role="separator"
          aria-orientation="horizontal"
          className="bg-gray-300 dark:bg-zinc-700 h-px my-2"
        />

        {/* Icon Selection Grid */}
        <div
          role="group"
          aria-label="Icon selection"
          className="grid grid-cols-[repeat(6,36px)] gap-1 py-3"
        >
          {WORKSPACE_ICONS.map((icon) => {
            const IconComponent = icon.component;
            const isSelected = currentIcon === icon.value;
            return (
              <button
                key={icon.name}
                role="menuitemradio"
                aria-checked={isSelected}
                tabIndex={0}
                className={cn(
                  "group m-0 flex h-9 w-9 items-center justify-center rounded-full p-0",
                  "origin-center active:scale-95 transition-transform",
                  "hover:bg-gray-100 dark:hover:bg-zinc-700",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400",
                  isSelected && "bg-gray-200 dark:bg-zinc-600"
                )}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  handleIconClick(icon);
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <span className="sr-only">{icon.name}</span>
                <IconComponent className="h-6 w-6" />
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

ColorIconChooser.propTypes = {
  isOpen: PropTypes.bool,
  onOpenChange: PropTypes.func,
  onColorSelect: PropTypes.func,
  onIconSelect: PropTypes.func,
  currentColor: PropTypes.string,
  currentIcon: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export default ColorIconChooser;
